import { useState, useRef, useMemo, useCallback } from "react";
import { RedirectIcon, ForwardIcon } from "components/icons";
import { Markdown } from "./Markdown";
import defaultConfig from "config";
import { IFetchErrorMessage } from "hooks/useGenerateImage";
import { useTextTruncation } from "hooks/useTextTruncation";

export const GenerateImageErrorCard = ({
  fetchErrorMessage,
  resetErrorMessage,
  onGenerateImage,
  onUpgrade,
}: {
  fetchErrorMessage: IFetchErrorMessage;
  resetErrorMessage?: () => void;
  onGenerateImage?: () => void;
  onUpgrade?: () => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  const { maxChars, isTruncated } = useTextTruncation({
    text: fetchErrorMessage?.prompt || "",
    maxLines: 3,
    reservedWidth: 80,
    containerRef,
    textRef,
  });

  // 计算显示文本
  const displayText = useMemo(() => {
    if (isExpanded || !isTruncated) return fetchErrorMessage?.prompt || "";
    return `${fetchErrorMessage?.prompt?.slice(0, maxChars)}...`;
  }, [isExpanded, isTruncated, fetchErrorMessage?.prompt, maxChars]);

  // 切换展开/收起状态
  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <div
      className="bg-[#fff] mt-3 p-3 rounded-[8px] max-h-fit border-[0.8px] border-solid border-[#94A3B8]"
      ref={containerRef}
    >
      <div className="flex flex-row items-center justify-between mb-2">
        <h3 className="text-[16px] font-semibold flex flex-row items-center gap-1 mb-3">
          Something went wrong 🫣
        </h3>
        <button
          className="flex flex-row items-center gap-2 text-[#4338CA] hover:bg-[#EEF2FF] p-2 rounded transition-all duration-300"
          onClick={() => resetErrorMessage?.()}
        >
          Back <ForwardIcon />
        </button>
      </div>

      {/* prompt */}
      <div className="text-[14px] text-[#2563EB] p-2 rounded-[4px] bg-[#EDF4FE] mb-[18px] leading-5">
        <span ref={textRef}>
          {`“${displayText}”` || ""}
          {isTruncated && !isExpanded && (
            <button
              className="ml-2 text-[#4338CA] underline"
              onClick={toggleExpand}
            >
              More
            </button>
          )}
          {isExpanded && (
            <button
              className="ml-2 text-[#4338CA] underline"
              onClick={toggleExpand}
            >
              Less
            </button>
          )}
        </span>
      </div>

      {/* error message */}
      <div className="flex flex-col gap-4 p-3 text-[#D97706] rounded-md border-[0.6px] border-dashed border-[#D97706] bg-[#FFFBEE]">
        <p className="font-semibold">
          {fetchErrorMessage?.title || "Error Generating Image"}
        </p>
        <div className="text-[14px] whitespace-pre-line leading-5">
          {fetchErrorMessage && (
            <Markdown message={fetchErrorMessage?.message} />
          )}
        </div>
        {(fetchErrorMessage?.action === "GetHelp" ||
          !fetchErrorMessage?.title) && (
          <a
            href={defaultConfig.canny_bug_url}
            target="_blank"
            rel="noreferrer"
            className="bg-[#171717] flex flex-row items-center justify-center py-2 px-4 gap-3 font-medium  text-white rounded"
          >
            Get Help
            <RedirectIcon />
          </a>
        )}
        {fetchErrorMessage?.action === "TryAgain" && (
          <button
            onClick={onGenerateImage}
            className="bg-[#171717] flex flex-row items-center justify-center py-2 px-4 gap-3 font-medium  text-white rounded"
          >
            Try Again
          </button>
        )}
        {fetchErrorMessage?.action === "UpgradePlan" && (
          <button
            onClick={onUpgrade}
            className="bg-[#171717] flex flex-row items-center justify-center py-2 px-4 gap-3 font-medium  text-white rounded"
          >
            Upgrade Plan
          </button>
        )}
      </div>
    </div>
  );
};
